import React, { useMemo } from 'react';
import { alpha, Box, Button, Grid, Menu, Stack, Typography } from '@mui/material';
import IconButton from "@mui/material/IconButton";
import CustomSvgIcons from '../../components/svg-icons';
import { useThemeMode } from '../../components/theme';
import { colours as constColours, CustomTooltip } from '../../consts';
import { FONT_SIZE_ACTION, FONT_SIZE_LEVELS, THEME_MODES } from '../../helpers/constant';
import { useThemeStore } from '../../zustand/store';
import FeatherIcon from 'feather-icons-react';

function AccessibilityMenuComponent() {
    const { themeMode, toggleTheme, colours } = useThemeMode();
    const { fontSizeLevel, setFontSizeLevel } = useThemeStore();
    const [anchorEl, setAnchorEl] = React.useState(null);
    const open = Boolean(anchorEl);

    const handleClick = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    // Color Mode Options
    const colorModeOptions = [
        {
            key: THEME_MODES.LIGHT,
            label: 'Light Mode',
            icon: CustomSvgIcons.sun,
            disabled: false,
        },
        {
            key: THEME_MODES.DARK,
            label: 'Dark Mode',
            icon: CustomSvgIcons.moon,
            disabled: false,
        },
        {
            key: THEME_MODES.HIGH_CONTRAST,
            label: 'High Contrast',
            icon: CustomSvgIcons.sunHighContrast,
            disabled: false,
        },
        {
            key: THEME_MODES.COLOR_VISION,
            label: 'Color Vision Deficiency',
            icon: CustomSvgIcons.colorVision,
            disabled: false,
        },
    ];

    // Text Size Options
    const textSizeOptions = useMemo(() => ([
        {
            label: FONT_SIZE_ACTION.NORMAL,
            action: () => setFontSizeLevel(FONT_SIZE_ACTION.NORMAL),
            icon: CustomSvgIcons.type,
            active: fontSizeLevel === FONT_SIZE_LEVELS.NORMAL,
            disabled: fontSizeLevel === FONT_SIZE_LEVELS.NORMAL,
        },
        {
            label: FONT_SIZE_ACTION.INCREASE,
            action: () => setFontSizeLevel(FONT_SIZE_ACTION.INCREASE),
            icon: CustomSvgIcons.typeIncrease,
            active: FONT_SIZE_LEVELS.NORMAL < fontSizeLevel,
            disabled: fontSizeLevel === FONT_SIZE_LEVELS.MAX,
        },
        {
            label: FONT_SIZE_ACTION.DECREASE,
            action: () => setFontSizeLevel(FONT_SIZE_ACTION.DECREASE),
            icon: CustomSvgIcons.typeDecrease,
            active: FONT_SIZE_LEVELS.NORMAL > fontSizeLevel,
            disabled: fontSizeLevel === FONT_SIZE_LEVELS.MIN,
        },
    ]), [fontSizeLevel, setFontSizeLevel]);

    const getButtonSx = (theme, isActive = false) => {
        if (isActive) return { borderRadius: 2, px: 1.5 };
        const isDark = theme.palette.mode === THEME_MODES.DARK;

        return {
            borderRadius: 2,
            px: 1.5,
            backgroundColor: isDark ? alpha(constColours.white, 0.05) : colours.gray5,
            borderColor: isDark ? alpha(constColours.white, 0.025) : colours.gray5,
            color: isDark ? colours.gray30 : colours.gray90,
        };
    };

    return (
        <>
            <CustomTooltip title="Accessibility Options">
                <IconButton
                    id="accessibility-button"
                    aria-controls={open ? 'account-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={open ? 'true' : undefined}
                    onClick={handleClick}
                    aria-label="Accessibility Options"
                    color="inherit"
                    sx={(theme) => ({
                        backgroundColor: open && (theme.palette.mode === THEME_MODES.LIGHT ? colours.grayDark500 : constColours.black)
                    })}
                >
                    {CustomSvgIcons.accessibility}
                </IconButton>
            </CustomTooltip>
            <Menu
                anchorEl={anchorEl}
                id="accessibility-menu"
                open={open}
                onClose={handleClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
                sx={{ marginTop: 1 }}
                slotProps={{
                    paper: {
                        sx: {
                            width: "550px",
                        },
                    },
                }}
                MenuListProps={{
                    disablePadding: true,
                    sx: {
                        padding: 4,
                    },
                }}
            >
                <Stack component="li" mb={3} direction="row" gap={1} justifyContent="space-between" alignItems="center">
                    <Typography variant="h5" fontWeight={600}>
                        Accessibility Options
                    </Typography>
                    <IconButton
                        onClick={handleClose}
                        aria-label="Close Accessibility Menu"
                        color="info"
                        size="small"
                    >
                        <FeatherIcon size={20} icon="x" />
                    </IconButton>
                </Stack>

                {/* Color Modes */}
                <Box component="li" mb={2.5}>
                    <Typography variant="body2" mb={1} fontWeight={500} textTransform="uppercase" letterSpacing={1.12}>
                        Color Adjustment Mode
                    </Typography>
                    <Grid container spacing={1}>
                        {colorModeOptions.map((item) => (
                            <Grid item xs={6} key={item.key}>
                                <Button
                                    variant="outlined"
                                    color={themeMode === item.key ? 'primary' : 'info'}
                                    onClick={() => toggleTheme(item.key)}
                                    fullWidth
                                    sx={(theme) => getButtonSx(theme, themeMode === item.key)}
                                    startIcon={item.icon}
                                    size="large"
                                    disabled={item.disabled}
                                >
                                    <Typography
                                        component="span"
                                        variant="body2"
                                        fontWeight={500}
                                        sx={{ color: themeMode === item.key ? colours.black : 'inherit', textAlign: 'left' }}
                                    >
                                        {item.label}
                                    </Typography>
                                </Button>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* Font Sizes */}
                <Box component="li">
                    <Typography variant="body2" mb={1} fontWeight={500} textTransform="uppercase" letterSpacing={1.12}>
                        Text Size
                    </Typography>
                    <Grid container spacing={1}>
                        {textSizeOptions.map((item) => (
                            <Grid item xs={6} key={item.label}>
                                <Button
                                    variant="outlined"
                                    color={item.active ? 'primary' : 'info'}
                                    onClick={() => item.action()}
                                    fullWidth
                                    disableRipple={item.disabled}
                                    sx={(theme) => ({
                                        ...getButtonSx(theme, item.active),
                                        ...(item.disabled && { cursor: 'not-allowed' })
                                    })}
                                    startIcon={item.icon}
                                    size="large"
                                >
                                    <Typography
                                        component="span"
                                        variant="body2"
                                        fontWeight={500}
                                        sx={{ color: item.active ? colours.black : 'inherit', textAlign: 'left' }}
                                    >
                                        {item.label}
                                    </Typography>
                                </Button>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Menu>
        </>
    );
}

export default AccessibilityMenuComponent;